import {z} from 'zod'
import {
    httpGetMedicalReportsByPatient,
    HttpGetMedicalReportsByPatientOptions
} from '@/features/medical-reports/http/medicalReports.http'
import {MedicalReportSmall} from '@/features/medical-reports/types'
import {QUERY_KEYS} from '@/queryClient'
import {PaginationResponse} from '@/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'

const ResponseShape = z.object({
    data: z.array(MedicalReportSmall),
    meta: PaginationResponse
})
type ResponseShape = z.infer<typeof ResponseShape>

export const useGetInfiniteMedicalReportsByPatient = ({
    urlParams,
    params,
    options
}: {
    urlParams: HttpGetMedicalReportsByPatientOptions['urlParams']
    params: Omit<HttpGetMedicalReportsByPatientOptions['params'], 'page'>
    options?: Pick<UseInfiniteQueryOptions<ResponseShape>, 'enabled'>
}) => {
    const query = useInfiniteQuery({
        queryKey: [
            QUERY_KEYS.PATIENT_MEDICAL_RECORD,
            urlParams,
            params.medicalRecordReportTypeId,
            params.createdByUserId,
            params.search,
            params.group,
            params.projectId,
            params.startDate,
            params.endDate
        ],
        queryFn: ({pageParam = 1}) =>
            httpGetMedicalReportsByPatient({
                urlParams,
                params: {
                    ...params,
                    page: pageParam
                }
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : []
    }
}
